/**
 * Board topology: the fixed, variant-independent geometry of a hex board.
 *
 * A `Board` is derived purely from its radius and never changes during a game, so it is
 * built once and shared. Piece placement lives elsewhere (see `position.ts`).
 *
 * Two bugs from the 2023 build are fixed here, and both were hot-path:
 *
 *   1. The old cache was a `WeakMap` keyed on the *variant object*. Every variant that
 *      arrived over the wire was a freshly-deserialised object, so the cache essentially
 *      never hit and the board was rebuilt constantly. Keyed by radius now.
 *   2. The old builder called `arr.indexOf(neighbour)` inside a loop over every cell and
 *      direction, making construction quadratic. A coordinate map makes it linear.
 */

import {
  cellCount,
  DIRECTION_COUNT,
  DIRECTIONS,
  type Hex,
  key,
  spiral,
  toPixel,
} from './hex.ts';

/** Sentinel stored in `neighbors` for a direction that leaves the board. */
export const OFF_BOARD = -1;

export const MIN_RADIUS = 2;
export const MAX_RADIUS = 6;

/**
 * Board topology in struct-of-arrays form. Flat typed arrays keep the movegen inner
 * loops allocation-free and cache-friendly; the object wrappers are for callers that
 * value readability over speed (rendering, tests, tutorial scripts).
 */
export interface Board {
  readonly radius: number;
  /** Number of cells. */
  readonly size: number;
  /** `3 * size` entries: q, r, s per cell. Radius is capped at 6, so Int8 suffices. */
  readonly qrs: Int8Array;
  /** `6 * size` entries: neighbour cell index per direction, or `OFF_BOARD`. */
  readonly neighbors: Int32Array;
  /** `2 * size` entries: x, y pixel centre per cell. */
  readonly xy: Float64Array;
  /** Cell index by `"q,r,s"` key, or `undefined` when off the board. */
  indexOf(k: string): number | undefined;
  /** Cell index by coordinate, or `OFF_BOARD` when off the board. */
  indexAt(q: number, r: number): number;
  /** Coordinate of a cell index. */
  hexAt(index: number): Hex;
  /** `"q,r,s"` key of a cell index. */
  keyAt(index: number): string;
  /** Neighbour of `index` in direction `dir`, or `OFF_BOARD`. */
  neighborOf(index: number, dir: number): number;
}

function build(radius: number): Board {
  const size = cellCount(radius);
  const qrs = new Int8Array(size * 3);
  const xy = new Float64Array(size * 2);
  const neighbors = new Int32Array(size * DIRECTION_COUNT).fill(OFF_BOARD);
  const byKey = new Map<string, number>();

  let i = 0;
  for (const h of spiral(radius)) {
    qrs[i * 3] = h.q;
    qrs[i * 3 + 1] = h.r;
    qrs[i * 3 + 2] = h.s;

    const { x, y } = toPixel(h);
    xy[i * 2] = x;
    xy[i * 2 + 1] = y;

    byKey.set(key(h), i);
    i++;
  }

  if (i !== size) {
    throw new Error(
      `Board radius ${radius}: spiral produced ${i} cells, expected ${size}`,
    );
  }

  // Linear pass: one map lookup per (cell, direction) instead of a linear scan.
  for (let c = 0; c < size; c++) {
    const q = qrs[c * 3];
    const r = qrs[c * 3 + 1];
    for (let d = 0; d < DIRECTION_COUNT; d++) {
      const nq = q + DIRECTIONS[d].q;
      const nr = r + DIRECTIONS[d].r;
      const n = byKey.get(`${nq},${nr},${-nq - nr}`);
      neighbors[c * DIRECTION_COUNT + d] = n === undefined ? OFF_BOARD : n;
    }
  }

  return {
    radius,
    size,
    qrs,
    neighbors,
    xy,
    indexOf: (k) => byKey.get(k),
    indexAt: (q, r) => byKey.get(`${q},${r},${-q - r}`) ?? OFF_BOARD,
    hexAt: (index) => ({
      q: qrs[index * 3],
      r: qrs[index * 3 + 1],
      s: qrs[index * 3 + 2],
    }),
    keyAt: (index) => `${qrs[index * 3]},${qrs[index * 3 + 1]},${qrs[index * 3 + 2]}`,
    neighborOf: (index, dir) => neighbors[index * DIRECTION_COUNT + dir],
  };
}

const cache = new Map<number, Board>();

/**
 * The board of a given radius. Boards are immutable and shared; there are at most five
 * of them in a running process.
 */
export function getBoard(radius: number): Board {
  if (!Number.isInteger(radius) || radius < MIN_RADIUS || radius > MAX_RADIUS) {
    throw new Error(
      `Board radius must be an integer in [${MIN_RADIUS}, ${MAX_RADIUS}], got ${radius}`,
    );
  }
  let board = cache.get(radius);
  if (!board) {
    board = build(radius);
    cache.set(radius, board);
  }
  return board;
}

/** Every cell index of a board, as a plain array. Convenience for tests and rendering. */
export const allCells = (board: Board): number[] =>
  Array.from({ length: board.size }, (_, i) => i);
